import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { getBookings, resetBookings } from '../../Store/StudentSlices/getBookingsSlice';
import { getStudents } from '../../Store/StudentSlices/getStudentsSlice';
import {
  resetScheduleSession,
  scheduleSession,
} from '../../Store/StudentSlices/scheduleSessionSlice';
import type { CalendarDay, ScheduleTab, Session, ViewMode } from './types';
import {
  addDays,
  buildCalendarDays,
  formatISODate,
  getBookingFocusArea,
  getBookingStudentName,
  groupSessionsByDate,
  mapBookingStatus,
  parseISODate,
  startOfDay,
  startOfWeek,
} from './utils';
import SessionsCalendar from './SessionsCalendar';
import SessionsList from './SessionsList';
import SessionsToolbar from './SessionsToolbar';
import SchedulePanel from './SchedulePanel';
import RecordingsView from './RecordingsView';

const SessionsView = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { bookings, loading: isBookingsLoading } = useAppSelector(
    (state) => state.getBookings
  );
  const {
    loading: isScheduling,
    error: scheduleError,
    errorMessage: scheduleErrorMessage,
  } = useAppSelector((state) => state.scheduleSession);
  const { students: studentsPayload, loading: isStudentsLoading } =
    useAppSelector((state) => state.getStudent);

  const [activeTab, setActiveTab] = useState<ScheduleTab>('All');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const view = searchParams.get('view');
    return view === 'calendar' ? 'calendar' : 'list';
  });
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [viewDate, setViewDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [selectedTime, setSelectedTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [studentId, setStudentId] = useState('');
  const [meetingPlatform, setMeetingPlatform] = useState('in-house');
  const [timezone, setTimezone] = useState('America/New_York');

  useEffect(() => {
    dispatch(getBookings());
    dispatch(getStudents());
  }, [dispatch]);

  const students = useMemo(() => {
    const payload =
      (studentsPayload as any)?.response ??
      (studentsPayload as any)?.data ??
      (studentsPayload as any)?.records ??
      studentsPayload ??
      [];
    return Array.isArray(payload) ? payload : [];
  }, [studentsPayload]);

  const studentOptions = useMemo(
    () =>
      students
        .map((student: any) => ({
          id: student.user_id ?? student.id ?? student.student_id ?? '',
          name:
            student.name ??
            student.full_name ??
            student.student_name ??
            'Student',
        }))
        .filter((student: any) => student.id),
    [students]
  );

  const sessions = useMemo<Session[]>(() => {
    console.log('🔍 [SESSIONS MEMO] Processing bookings:', bookings?.length || 0);

    if (!bookings || bookings.length === 0) {
      console.log('🔍 [SESSIONS MEMO] No bookings found');
      return [];
    }

    const allSessions: Session[] = [];

    bookings.forEach((booking) => {
      const bookingValue = booking as any;

      // Check if booking has sessions array
      if (booking.sessions && booking.sessions.length > 0) {
        console.log(`🔍 [SESSIONS MEMO] Booking ${bookingValue.id} has ${booking.sessions.length} sessions:`, booking.sessions);
        // Create a session entry for each session in the sessions array
        booking.sessions.forEach((session) => {
          const sessionDate = new Date(session.scheduled_time);
          const dateKey = formatISODate(sessionDate);

          let start: string;
          let end: string;

          // Backend provides formatted "10:00 AM" - convert to 24h format
          if (session.time) {
            const timeMatch = session.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
            if (timeMatch) {
              let hours = parseInt(timeMatch[1], 10);
              const minutes = timeMatch[2];
              const period = timeMatch[3].toUpperCase();
              if (period === 'PM' && hours !== 12) hours += 12;
              if (period === 'AM' && hours === 12) hours = 0;
              start = `${hours.toString().padStart(2, '0')}:${minutes}`;

              // Calculate end time
              const duration = session.duration || 60;
              const totalMinutes = hours * 60 + parseInt(minutes, 10) + duration;
              const endHours = Math.floor(totalMinutes / 60) % 24;
              const endMinutes = totalMinutes % 60;
              end = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
            } else {
              // Fallback: parse timestamp
              const startHours = sessionDate.getHours();
              const startMinutes = sessionDate.getMinutes();
              start = `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
              const endDate = new Date(sessionDate.getTime() + 60 * 60 * 1000);
              end = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
            }
          } else {
            // Fallback: parse timestamp
            const startHours = sessionDate.getHours();
            const startMinutes = sessionDate.getMinutes();
            start = `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;
            const endDate = new Date(sessionDate.getTime() + ((session.duration || 60) * 60 * 1000));
            end = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
          }

          allSessions.push({
            id: session.id,
            date: dateKey,
            start,
            end,
            student: getBookingStudentName(bookingValue),
            focus: getBookingFocusArea(bookingValue),
            status: mapBookingStatus(session.status || booking.session_status || 'scheduled'),
            joining_link: session.joining_link,
          });
        });
      }
      // Also check if booking has start_time directly (virtual booking format)
      else if (booking.start_time && booking.session_status) {
        const sessionDate = new Date(booking.start_time);
        const dateKey = formatISODate(sessionDate);
        const startHours = sessionDate.getHours();
        const startMinutes = sessionDate.getMinutes();
        const start = `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`;

        let end: string;
        if (booking.end_time) {
          const endDate = new Date(booking.end_time);
          end = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
        } else {
          const endDate = new Date(sessionDate.getTime() + 60 * 60 * 1000);
          end = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
        }

        allSessions.push({
          id: booking.id,
          date: dateKey,
          start,
          end,
          student: getBookingStudentName(bookingValue),
          focus: getBookingFocusArea(bookingValue),
          status: mapBookingStatus(booking.session_status || ''),
          joining_link: booking.joining_link,
        });
      }
    });

    // Sort by date and time in reverse chronological order (latest first)
    console.log(`🔍 [SESSIONS MEMO] Total sessions created: ${allSessions.length}`);
    console.log('🔍 [SESSIONS MEMO] All sessions:', allSessions);
    return allSessions.sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date); // Descending: latest dates first
      if (dateCompare !== 0) return dateCompare;
      return b.start.localeCompare(a.start); // Then by time within same date (latest first)
    });
  }, [bookings]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const now = useMemo(() => new Date(), []);

  const filteredSessions = useMemo(() => {
    console.log(`🎯 [FILTERED SESSIONS] Active tab: ${activeTab}, Total sessions: ${sessions.length}`);

    // Show all sessions for 'All' tab
    if (activeTab === 'All') {
      console.log('🎯 [FILTERED SESSIONS] Showing all sessions:', sessions.length);
      return sessions;
    }

    const filtered = sessions.filter((session) => {
      const sessionDate = parseISODate(session.date);
      const sessionDateTime = new Date(sessionDate);

      // Parse the time and add it to the date
      const [hours, minutes] = session.start.split(':').map(Number);
      sessionDateTime.setHours(hours, minutes, 0, 0);

      const isPast = sessionDateTime < now;
      const isFuture = sessionDateTime >= now;

      if (activeTab === 'Upcoming') {
        // Show scheduled and active sessions that are in the future
        return isFuture && ['Scheduled', 'Active'].includes(session.status);
      }

      if (activeTab === 'Completed') {
        // Show completed sessions or past active sessions
        return session.status === 'Completed' || (isPast && session.status === 'Active');
      }

      if (activeTab === 'Missed') {
        // Show missed, cancelled sessions, or scheduled sessions that are past their time
        return session.status === 'Missed' ||
          session.status === 'Cancelled' ||
          (isPast && session.status === 'Scheduled');
      }

      return false;
    });

    console.log(`🎯 [FILTERED SESSIONS] Filtered for ${activeTab}: ${filtered.length} sessions`);
    return filtered;
  }, [activeTab, sessions, now]);

  const sessionGroups = useMemo(() => {
    const groups = groupSessionsByDate(filteredSessions, today);
    console.log(`📊 [SESSION GROUPS] Created ${groups.length} groups from ${filteredSessions.length} sessions`);
    groups.forEach((group, idx) => {
      console.log(`📊 [SESSION GROUPS] Group ${idx + 1}: ${group.label} - ${group.sessions.length} sessions`, group.sessions);
    });
    return groups;
  }, [filteredSessions, today]);

  const calendarDays = useMemo(() => buildCalendarDays(viewDate), [viewDate]);

  const monthLabel = viewDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const weekStart = useMemo(() => startOfWeek(calendarDate), [calendarDate]);
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const weekSessions = useMemo(() => {
    const weekEnd = addDays(weekStart, 6);
    return filteredSessions.filter((session) => {
      const sessionDate = parseISODate(session.date);
      return sessionDate >= weekStart && sessionDate <= weekEnd;
    });
  }, [filteredSessions, weekStart]);

  const calendarMonthLabel = calendarDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const canSchedule =
    studentId.trim().length > 0 && selectedTime.trim().length > 0 && duration > 0;

  const resetScheduleForm = () => {
    setSelectedDate(new Date());
    setSelectedTime('');
    setDuration(60);
    setStudentId('');
    setMeetingPlatform('in-house');
    setTimezone('America/New_York');
    dispatch(resetScheduleSession());
  };

  const handleToggleSchedule = () => {
    setIsScheduleOpen((prev) => {
      const next = !prev;
      if (!next) {
        resetScheduleForm();
      }
      return next;
    });
  };

  const handleSchedule = async () => {
    if (!canSchedule || isScheduling) {
      return;
    }

    const formattedTime =
      selectedTime.length === 5 ? `${selectedTime}:00` : selectedTime;

    const sessionData = {
      studentId: studentId.trim(),
      date: formatISODate(selectedDate),
      time: formattedTime,
      meetingPlatform,
      duration,
      status: 'scheduled',
      timezone,
    };

    console.log('📅 [USER SELECTION] Booking session with:', {
      selectedDate,
      selectedTime,
      formattedTime,
      meetingPlatform,
      duration,
      timezone,
      formattedDate: formatISODate(selectedDate),
      allData: sessionData,
    });

    try {
      await dispatch(scheduleSession(sessionData)).unwrap();
      // Clear cache and refresh bookings to show the new session
      dispatch(resetBookings());
      dispatch(getBookings());
      setIsScheduleOpen(false);
      resetScheduleForm();
    } catch {
      // Error state is handled by the slice.
    }
  };

  const handleDateSelect = (day: CalendarDay) => {
    setSelectedDate(day.date);
    setCalendarDate(day.date);
    if (!day.inCurrentMonth) {
      setViewDate(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
    }
  };

  return (
    <div className="flex flex-col gap-6 h-full overflow-hidden">
      <div className={`flex flex-col gap-6 h-full overflow-hidden ${isScheduleOpen ? 'xl:flex-row' : ''}`}>
        <section className="flex min-w-0 flex-1 flex-col gap-6 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm h-full overflow-hidden">
          <SessionsToolbar
            data={{ activeTab, viewMode }}
            actions={{
              onTabChange: setActiveTab,
              onToggleSchedule: handleToggleSchedule,
              onViewModeChange: setViewMode,
            }}
          />

          {activeTab === 'Recordings' ? (
            <RecordingsView />
          ) : viewMode === 'list' ? (
            <SessionsList
              data={{ isLoading: isBookingsLoading, groups: sessionGroups, activeTab }}
            />
          ) : (
            <SessionsCalendar
              data={{
                isLoading: isBookingsLoading,
                monthLabel: calendarMonthLabel,
                weekDates,
                weekSessions,
              }}
              actions={{
                onPrevWeek: () => setCalendarDate((prev) => addDays(prev, -7)),
                onNextWeek: () => setCalendarDate((prev) => addDays(prev, 7)),
                onToday: () => setCalendarDate(new Date()),
              }}
            />
          )}
        </section>

        <SchedulePanel
          data={{
            isOpen: isScheduleOpen,
            monthLabel,
            calendarDays,
            selectedDate,
            selectedTime,
            duration,
            studentId,
            studentOptions,
            meetingPlatform,
            timezone,
            isStudentsLoading,
            canSchedule,
            isScheduling,
            scheduleError,
            scheduleErrorMessage,
          }}
          actions={{
            onClose: () => {
              setIsScheduleOpen(false);
              resetScheduleForm();
            },
            onPrevMonth: () =>
              setViewDate(
                (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
              ),
            onNextMonth: () =>
              setViewDate(
                (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
              ),
            onDateSelect: handleDateSelect,
            onTimeChange: setSelectedTime,
            onIncreaseDuration: () =>
              setDuration((prev) => Math.min(240, prev + 15)),
            onDecreaseDuration: () =>
              setDuration((prev) => Math.max(15, prev - 15)),
            onStudentChange: setStudentId,
            onMeetingPlatformChange: setMeetingPlatform,
            onTimezoneChange: setTimezone,
            onSchedule: handleSchedule,
            onReset: resetScheduleForm,
          }}
        />
      </div>
    </div>
  );
};

export default SessionsView;
